import { useCallback, useEffect, useState } from "react";
import type { Bracket } from "../types";
import { isRoundComplete } from "../data";
import { generateNextRound } from "../pairings";
import StandingsTable from "./StandingsTable";
import PairingsPanel from "./PairingsPanel";
import { Button, Card, Col, Nav, Row, Stack } from "react-bootstrap";

interface BracketViewProps {
  bracket: Bracket;
  setBracket: React.Dispatch<React.SetStateAction<Bracket>>;
  onReset: () => void;
}

/** Displays the bracket rounds, matches, and standings once a tournament is in progress. */
export default function BracketView({
  bracket,
  setBracket,
  onReset,
}: BracketViewProps) {
  const { players, rounds, config } = bracket;
  const activeRoundIdx = rounds.length - 1;
  const activeRound = rounds[activeRoundIdx];
  const allReported = activeRound ? isRoundComplete(activeRound) : false;
  const allRoundsDone = rounds.length >= config.numRounds && allReported;

  const [selectedRound, setSelectedRound] = useState(activeRoundIdx);

  // Auto-jump to the newest round when one is generated
  useEffect(() => {
    setSelectedRound(activeRoundIdx);
  }, [activeRoundIdx]);

  const handleReport = useCallback(
    (matchId: string, g1: number, g2: number) => {
      setBracket((prev) => ({
        ...prev,
        rounds: prev.rounds.map((round) => ({
          ...round,
          matches: round.matches.map((m) =>
            m.id === matchId ? { ...m, player1Games: g1, player2Games: g2 } : m,
          ),
        })),
      }));
    },
    [setBracket],
  );

  const handleNextRound = useCallback(() => {
    setBracket((prev) => {
      const newRound = generateNextRound(
        prev.players,
        prev.rounds,
        prev.config.bestOf,
      );
      return { ...prev, rounds: [...prev.rounds, newRound] };
    });
  }, [setBracket]);

  const visibleRound = rounds[selectedRound];
  const isViewingActive = selectedRound === activeRoundIdx && !allRoundsDone;

  // Standings reflect the round currently being viewed in the tabs
  const standingsRoundCount = selectedRound + 1;
  const standingsTitle = allRoundsDone && selectedRound === activeRoundIdx
    ? "Final Standings"
    : `Standings · After Round ${visibleRound?.roundNum ?? "—"}`;

  return (
    <Card className="bracket-card mx-auto mb-4">
      <Card.Header className="py-3">
        <Stack direction="horizontal" gap={3} className="align-items-center">
          <div className="me-auto">
            <h2 className="h5 mb-1">Swiss Bracket</h2>
            <div className="text-muted small">
              {players.length} players · {config.numRounds} rounds · Bo
              {config.bestOf} · Top {config.topCutCount} advance
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={onReset}>
            Back to Setup
          </Button>
        </Stack>
      </Card.Header>

      <Card.Body className="bracket-body">
        <Row className="g-3">
          <Col lg={8} className="bracket-main-col">
            <Nav
              variant="tabs"
              activeKey={String(selectedRound)}
              onSelect={(k) => k && setSelectedRound(Number(k))}
              className="mb-3 flex-nowrap round-tabs"
            >
              {rounds.map((round, i) => {
                const complete = isRoundComplete(round);
                const isLive = i === activeRoundIdx && !allRoundsDone;
                return (
                  <Nav.Item key={round.roundNum}>
                    <Nav.Link
                      eventKey={String(i)}
                      className="text-nowrap d-flex align-items-center gap-2"
                    >
                      <span>Round {round.roundNum}</span>
                      {isLive ? (
                        <span className="stamp stamp--live">Live</span>
                      ) : complete ? (
                        <span className="stamp stamp--done">Done</span>
                      ) : null}
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
            </Nav>

            {visibleRound && (
              <PairingsPanel
                round={visibleRound}
                players={players}
                onReport={handleReport}
                bestOf={config.bestOf}
                isActive={isViewingActive}
              />
            )}

            <div className="mt-3 d-flex justify-content-center">
              {isViewingActive && allReported && !allRoundsDone && (
                <Button
                  variant="primary"
                  size="lg"
                  className="px-4"
                  onClick={handleNextRound}
                >
                  Generate Round {rounds.length + 1}
                </Button>
              )}

              {allRoundsDone && selectedRound === activeRoundIdx && (
                <Card className="w-100">
                  <Card.Body className="text-center py-3">
                    <div className="mb-2">
                      <span className="stamp stamp--done">Done</span>
                    </div>
                    <div className="fw-bold mb-1">Bracket Complete</div>
                    <div className="small text-muted">
                      Top {config.topCutCount} players advance to the main
                      bracket.
                    </div>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>

          <Col lg={4}>
            <div className="standings-sidebar">
              <StandingsTable
                rounds={rounds.slice(0, standingsRoundCount)}
                players={players}
                title={standingsTitle}
                advancementCount={config.topCutCount}
              />
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
